<template>
  <component :is="asForm ? BaseForm : 'div'" ref="attributeSelectionFormRef" :form="attributeSelection">
    <div :class="brandClasses.variantSelectionWrapper">
      <section
        v-for="({ groupBy, groupedOptions, isSingleton, label, type }, i) in variantAttributes"
        :key="type"
        :class="{ '<lg:op-0': showOnlyFirstSelectorOnMobile && i !== 0 }"
        :data-scroll-el="`${type}Pickers`"
        :data-test-id="`${type}-groups`"
      >
        <div
          v-if="!(isDummyCustoms && type === 'color')"
          :class="{
            'flex items-center space-x-2': showBadge,
            'hidden': !showSingleAttributes && type !== 'color' && isSingleton,
          }"
        >
          <div v-if="showBadge" class="h-4 w-4 flex justify-center rounded-full bg-grey-10">
            <span class="text-xs c-white fw-bold">{{ i + 1 }}</span>
          </div>
          <div
            :class="[
              brandClasses.variantSelectionLabel.base,
              type === 'size' && brandClasses.variantSelectionLabel.size,
            ]"
          >
            <div class="flex gap-1">
              <h2 :id="label" class="fw-bold">
                {{ isDigitalProduct && type === 'size' ? $t.amount : label }}<span aria-hidden="true">:</span>
              </h2>
              {{ attributeSelectionLabels[type] }}
              <template v-if="type !== 'color' && showStockStatusLabel">
                - <span class="c-red-30">{{ selectedVariant?.stockStatusLabel }}</span>
              </template>
            </div>
            <slot v-if="type === 'size'" name="size-and-fit" />
          </div>
        </div>
        <vf-form-error v-if="type !== 'color' && error && !attributeSelection[type]" class="mt-1">
          {{ isDigitalProduct ? $t.selectCardAmountError : $t.selectSizeError }}
        </vf-form-error>
        <template v-if="!(isDummyCustoms && type === 'color')">
          <div
            v-for="({ groupValue, options }, j) in groupedOptions"
            :key="j"
            class="mt-2"
            :class="{
              [brandClasses.variantSelectionGroupedOptionsGreaterThanOne!]: j !== groupedOptions.length - 1,
              hidden: !showSingleAttributes && type !== 'color' && isSingleton,
            }"
            :data-test-id="`${type}-groups-item`"
          >
            <product-pricing
              v-if="groupValue && groupedOptions.length > 1 && groupBy === 'price'"
              :class="brandClasses.variantSelectionGroupedOptionsPrice"
              :currency="product.currency"
              :price="groupValue"
            />
            <component
              :is="options.length > swatches.collapseThreshold ? BaseCollapse : 'div'"
              v-if="type === 'color'"
              v-bind="options.length > swatches.collapseThreshold && {
                classToggle: 'self-end',
                classContent: isRectangle ? 'max-h-48' : 'max-h-54 lg:max-h-75',
                disableVisibilityToggle: true,
                inverted: true,
              }"
            >
              <fieldset :aria-labelledby="label" class="flex wrap" :class="{ 'gap-1': isRectangle }">
                <vf-color-picker
                  v-for="{ available, id, label: optionLabel, swatch, url, value } in options"
                  :key="optionLabel"
                  :active="!getIsPending(value, id, pending) && getIsActive(value, id)"
                  :color="swatch?.color"
                  :loading="getIsPending(value, id, pending)"
                  :name="optionLabel"
                  :size="colorPickerSize"
                  :thumbnail="swatch?.image"
                  :unavailable="!isComingSoon && (!available && !getIsPending(value, id, pending))"
                  :variant="colorPickerShape"
                  @click="selectNewColor(enableColorAsAttribute ? value : id, url, optionLabel)"
                />
              </fieldset>
              <!-- toggle for base-collapse -->
              <template #toggle="{ expanded }">
                <div>
                  <p class="py-2 text-right text-sm lh-3 underlined lg:py-4">
                    {{ expanded ? $t.viewLess : $t.viewMore }}
                  </p>
                  <vf-icon
                    v-if="swatches.showCollapseIcon"
                    class="ml-1"
                    :dir="expanded ? 'up' : 'down'"
                    name="chevron"
                    size="md"
                  />
                </div>
              </template>
            </component>
            <fieldset v-else :aria-labelledby="label">
              <base-form-field v-slot="{ attrs }" :name="type" :rule="validateRequired()" :selector="`${type}Pickers`">
                <div class="flex gap-2 wrap">
                  <vf-size-picker
                    v-for="({ available, label: optionLabel, id, value, productUrl }, n) in options"
                    :key="optionLabel"
                    :class="{ 'pointer-events-none': !allowOutOfStockSelection && !getAttrOptionAvailable(type, value) && !getAttrOptionNotifyMe(type, value) }"
                    :model-value="attributeSelection[type]"
                    :name="label"
                    :required="n === 0"
                    :type
                    :unavailable="isSplitVariant(productUrl)
                      && !getIsPending(value, id, pending)
                      && (!available || !getAttrOptionAvailable(type, value))"
                    :value
                    v-bind="attrs"
                    @input="isSplitVariant(productUrl) && $emit('selectSize', type, value, optionLabel)"
                  >
                    <template v-if="isSplitVariant(productUrl)">
                      {{ isDigitalProduct ? getDigitalProductOptionLabel(optionLabel) : optionLabel }}
                    </template>
                    <base-link
                      v-else
                      :to="{
                        path: productUrl,
                        state: history.state,
                      }"
                      @click="selectExtendedSize(label, optionLabel)"
                    >
                      {{ optionLabel }}
                    </base-link>
                  </vf-size-picker>
                </div>
              </base-form-field>
            </fieldset>
            <slot v-if="type === 'size'" name="sizing-info" />
          </div>
        </template>
        <vf-link
          v-if="$feature.showCustomizeOnPDP && product.customsExternalUrl && type === 'color'"
          class="items-center gap-1 text-sm fw-bold lh-3 i-flex hover:c-brand-3"
          :class="{ 'mt-4': !isDummyCustoms }"
          :to="product.customsExternalUrl"
          @click="$gtm.push('pdpPage.onCustomize')"
        >
          <vf-icon name="customs" size="md" /> {{ $t.customize }}
        </vf-link>
        <div v-if="isComingSoon" class="mt-4 space-y-4">
          <p class="title-4 c-grey-20">
            {{ comingSoonText }}
          </p>
          <p v-if="selectedVariant?.notifyMe">
            {{ replaceAll($t.comingSoonDescription, { productName: product.name }) }}
          </p>
        </div>
      </section>
    </div>
  </component>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { Responsive } from '#types/common'
import type {
  AttributeSelection,
  ColorSwatchShape,
  ProductAttributeType,
  ProductExtended,
  SelectedVariant
} from '#types/product'
import type { SizesSubUnion } from '#types/sizes'
import { BaseCollapse, BaseForm } from '#components'

export type Props = {
  /*
  * variants, showNotifyMe, productInventory props are added only for consistency with other brands
  * They aren't used in the component
  * but allows to avoid situations with incorrect props resolution when rendering the DOM
  */
  attributeSelectionLabels: Record<string, string>
  colorSize?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  colorSwatch?: ColorSwatchShape
  error?: boolean
  isComingSoon?: boolean
  isDigitalProduct?: boolean
  getAttrOptionAvailable?: (type: ProductAttributeType, value: string) => boolean | undefined
  getAttrOptionNotifyMe?: (type: ProductAttributeType, value: string) => boolean | undefined
  pending?: boolean
  product: ProductExtended
  selectedVariant?: SelectedVariant
  showBadge?: boolean
  showOnlyFirstSelectorOnMobile?: boolean
  showSingleAttributes?: boolean
  asForm?: boolean
  showNotifyMe?: boolean
  variants?: any
}

export type Emits = {
  selectColor: [id: string, url: string, optionLabel: string]
  selectSize: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
}

const {
  colorSize,
  colorSwatch,
  isComingSoon,
  getAttrOptionAvailable = () => true,
  getAttrOptionNotifyMe = () => true,
  product,
  selectedVariant,
  showSingleAttributes = true
} = defineProps<Props>()

const emit = defineEmits<Emits>()

const { brandClasses, enableColorAsAttribute, swatches, allowOutOfStockSelection } = useAppConfig().pages.pdp || {}
const { $feature, $gtm, $t } = useNuxtApp()
const route = useRoute()
const { history } = useRouter().options
const attributeSelectionFormRef = defineModel<InstanceType<typeof BaseFormType> | null>('formRef', { required: false })

const colorPickerShape = colorSwatch ?? swatches.variant
const colorPickerSize = colorSize ?? swatches.size
const isRectangle = colorPickerShape === 'rectangle'
const attributeSelection = defineModel<AttributeSelection>({ required: true })

const selectedProductId = ref()

const variantAttributes = computed(() =>
  isComingSoon ? product.attributes.filter((attribute) => attribute.type === 'color') : product.attributes)

const isCurrentUrl = (productUrl?: string) => productUrl === route.path
const isSplitVariant = (productUrl?: string) => !productUrl || isCurrentUrl(productUrl)

const selectNewColor = (id, url, optionLabel) => {
  selectedProductId.value = id
  emit('selectColor', id, url, optionLabel)
}

const getDigitalProductOptionLabel = (optionLabel: string) => useFormattedPrice(
  Number.parseInt(optionLabel),
  product.currency,
  { trailingZeroDisplay: 'stripIfInteger' }
)

const getIsActive = (value: string, id: string) =>
  enableColorAsAttribute ? attributeSelection.value.color === value : product.id === id

const getIsPending = (value: string, id: string, pending?: boolean) => {
  if (!pending) return

  return enableColorAsAttribute
    ? (attributeSelection.value.color === value && id !== product.id)
    : selectedProductId.value === id
}

const selectExtendedSize = (attrName: string, label: string) => {
  $gtm.push('pdpPage.onSelectSize', `${product.id} - ${label}`, attrName)
}

const isDummyCustoms = computed(() =>
  product.attributes.find(({ type }) => type === 'color')?.options?.some(({ label }) => label === 'Customs')
)

const comingSoonText = computed(() => {
  let text = $t.comingSoon
  if (product.targetPublishDate)
    text += ` - ${formatTargetPublishDate(product.targetPublishDate, $feature.configProductComingSoonDateFormat)}`
  return text
})

const showStockStatusLabel = computed<boolean>(() =>
  $feature.showSizeLevelStockMessagingOnPDP && !!selectedVariant?.stockStatusLabel)

defineExpose({
  validate: () => attributeSelectionFormRef.value?.validate()
})
</script>
